/**
 * User-facing copy for file-library writes the server refuses on permission
 * grounds (403).
 *
 * Lives in `_core` because both layers need the same sentence: the service
 * reports the refused folder creation itself, while the refused upload surfaces
 * through the page's error mapping. Keeping one definition keeps the two from
 * drifting and keeps translators to one key per message.
 *
 * Two distinct server rejections map onto each message - a read-only viewer
 * writing into a folder ("Admin access required for this operation") and a
 * non-platform admin writing at the library root ("Root-level writes require
 * platform admin access"). Both mean the same thing to the user, and neither
 * server message is fit to show, being developer-facing.
 */

/**
 * Copy for a refused upload.
 *
 * Deliberately names no file: a refused batch fails as a whole, and the
 * individual file is not what the user has to act on.
 */
export function uploadPermissionDeniedMessage(): string {
	return $localize`:File Library permission error@@fileLibraryUploadPermissionDenied:You don't have permission to upload to this folder. Contact your administrator for access.`;
}

/** Copy for a refused folder creation. Counterpart of {@link uploadPermissionDeniedMessage}. */
export function createFolderPermissionDeniedMessage(): string {
	return $localize`:File Library permission error@@fileLibraryCreateFolderPermissionDenied:You don't have permission to create a folder here. Contact your administrator for access.`;
}

/**
 * Copy for a refused change to existing content - rename, move, copy, delete.
 *
 * One sentence for all four: they share a single server rule (admin on the folder)
 * and the user's remedy is the same whichever one they tried. The actions are hidden
 * from a viewer, so this is the backstop for a stale role - a grant revoked while the
 * page was open, or a view whose items span folders the role does not describe.
 */
export function modifyPermissionDeniedMessage(): string {
	return $localize`:File Library permission error@@fileLibraryModifyPermissionDenied:You don't have permission to change this folder. Contact your administrator for access.`;
}
